import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { auditTrailAPI } from '../services/accountingService';
import campusService from '../services/campusService';
import { Student, Users, Buildings, CheckCircle, Plus, Megaphone, Warning, X, ClipboardText, MapPin } from '@phosphor-icons/react';

const formatAuditLabel = (value) => (value || '').replace(/_/g, ' ');

const formatAuditTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMins = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.round(diffMins / 60)}h ago`;
  return d.toLocaleDateString('en-GH', { month: 'short', day: 'numeric' });
};

function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAcademicTerm, setHasAcademicTerm] = useState(true);
  const [auditActivity, setAuditActivity] = useState([]);
  const [auditTotalActions, setAuditTotalActions] = useState(null);
  const [campusName, setCampusName] = useState(null);
  const isFinanceAdmin = user && ['super_admin', 'school_admin', 'hr'].includes(user.role);

  useEffect(() => {
    // Check if user has required role
    const dashboardRoles = ['super_admin', 'school_admin', 'teacher'];
    if (user && !dashboardRoles.includes(user.role)) {
      const roleRedirects = {
        student:          '/student-portal',
        parent:           '/parent-portal',
        security_officer: '/security',
        driver:           '/security/driver',
        hr:               '/hr-portal',
        nurse:            '/nurse-portal',
        registrar:        '/registrar-portal',
        storekeeper:      '/storekeeper-portal',
        canteen_staff:    '/canteen-admin',
      };
      navigate(roleRedirects[user.role] || '/');
      return;
    }

   

    api.get('/api/dashboard/overview')
      .then(data => {
        setStats(data);
        setHasAcademicTerm(data.has_current_term !== false);
      })
      .catch(err => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));

    if (user && ['super_admin', 'school_admin', 'hr'].includes(user.role)) {
      auditTrailAPI.getAuditSummary()
        .then(summary => setAuditTotalActions(summary?.total_actions ?? 0))
        .catch(() => {});
      auditTrailAPI.getRecentActivity(24, 5)
        .then(logs => setAuditActivity(Array.isArray(logs) ? logs : []))
        .catch(() => {});
    }

    if (user?.campus_id) {
      campusService.listCampuses()
        .then(campuses => setCampusName(Array.isArray(campuses) && campuses[0] ? campuses[0].name : null))
        .catch(() => {});
    }
  }, [user, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading dashboard...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard" data-testid="dashboard">
        {/* Academic Term Setup Banner — only shown when the school genuinely has no current term */}
        {!hasAcademicTerm && (
          <div className="alert-banner alert-warning">
            <Warning size={24} weight="fill" />
            <div>
              <h3>Academic Term Setup Required</h3>
              <p>
                Dear School Administrator, please set up an academic term first to enable proper functioning of the platform. All other configurations (fees, assignments, grades, attendance records) depend on academic terms.
              </p>
            </div>
          </div>
        )}

        <div className="dashboard-hero">
          <h1>{t('dashboard.welcomeBack')}, {user?.first_name}!</h1>
          <p>{t('dashboard.overview')}</p>
          {user?.campus_id && (
            <span className="campus-badge">
              <MapPin size={13} weight="fill" /> {campusName || 'Campus-scoped view'}
            </span>
          )}
        </div>

        {stats?.stats && (
          <div className="dash-stats-grid">
            <div className="stat-card" data-testid="stat-students">
              <div className="stat-icon dash-icon-students">
                <Student size={28} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>{stats.stats.total_students || 0}</h3>
                <p>Total Students</p>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-teachers">
              <div className="stat-icon dash-icon-teachers">
                <Users size={28} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>{stats.stats.total_teachers || 0}</h3>
                <p>Teachers</p>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-classes">
              <div className="stat-icon dash-icon-classes">
                <Buildings size={28} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>{stats.stats.total_classes || 0}</h3>
                <p>Classes</p>
              </div>
            </div>
            
            <div className="stat-card" data-testid="stat-attendance">
              <div className="stat-icon dash-icon-attendance">
                <CheckCircle size={28} weight="duotone" />
              </div>
              <div className="stat-content">
                <h3>{stats.stats.attendance_today?.attendance_rate || 0}%</h3>
                <p>Today's Attendance</p>
              </div>
            </div>
          </div>
        )}

        {stats?.stats?.fees && (
          <div className="dashboard-section">
            <h2>Fee Collection Overview</h2>
            <div className="fee-stats">
              <div className="fee-stat">
                <span className="fee-label">Total Fees</span>
                <span className="fee-value">GHS {stats.stats.fees.total?.toLocaleString() || 0}</span>
              </div>
              <div className="fee-stat">
                <span className="fee-label">Collected</span>
                <span className="fee-value collected">GHS {stats.stats.fees.collected?.toLocaleString() || 0}</span>
              </div>
              <div className="fee-stat">
                <span className="fee-label">Outstanding</span>
                <span className="fee-value outstanding">GHS {stats.stats.fees.outstanding?.toLocaleString() || 0}</span>
              </div>
              <div className="fee-stat">
                <span className="fee-label">Collection Rate</span>
                <span className="fee-value rate">{stats.stats.fees.collection_rate || 0}%</span>
              </div>
            </div>
          </div>
        )}

        {stats?.stats?.finance && (
          <div className="dashboard-section">
            <h2>Revenue</h2>
            <div className="fee-stats">
              <div className="fee-stat">
                <span className="fee-label">Net Profit</span>
                <span className={`fee-value ${stats.stats.finance.net_profit >= 0 ? 'collected' : 'outstanding'}`}>
                  GHS {stats.stats.finance.net_profit?.toLocaleString() || 0}
                </span>
              </div>
              <div className="fee-stat">
                <span className="fee-label">Profit Margin</span>
                <span className="fee-value rate">{stats.stats.finance.profit_margin || 0}%</span>
              </div>
              {stats.stats.finance.revenue_by_source?.length > 0 && (
                <div className="fee-stat">
                  <span className="fee-label">Top Revenue Source</span>
                  <span className="fee-value">{stats.stats.finance.revenue_by_source[0].name}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {isFinanceAdmin && (
          <div className="dashboard-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClipboardText size={20} weight="duotone" /> GL Audit Activity
                {auditTotalActions !== null && (
                  <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>
                    · {auditTotalActions} actions in the last 30 days
                  </span>
                )}
              </h2>
              <Link to="/finance?tab=audit" style={{ fontSize: 13 }}>View Full Audit Log →</Link>
            </div>
            {auditActivity.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: '8px 0 0' }}>
                No accounting changes in the last 24 hours.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {auditActivity.map(entry => (
                  <div key={entry.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13,
                  }}>
                    <span style={{ flex: 1 }}>
                      <strong style={{ textTransform: 'capitalize' }}>{formatAuditLabel(entry.action)}</strong>
                      {' on '}{formatAuditLabel(entry.entity_type)}
                      {entry.user_name && <> by {entry.user_name}</>}
                      {entry.change_summary && <span style={{ color: '#6b7280' }}> — {entry.change_summary}</span>}
                    </span>
                    <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: 12 }}>
                      {formatAuditTime(entry.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-grid">
            <Link to="/students" className="action-card" data-testid="quick-add-student">
              <span className="action-icon"><Student size={24} weight="duotone" /></span>
              <span>Add Student</span>
            </Link>
            <Link to="/staff" className="action-card" data-testid="quick-add-staff">
              <span className="action-icon"><Users size={24} weight="duotone" /></span>
              <span>Add Staff</span>
            </Link>
            <Link to="/attendance" className="action-card" data-testid="quick-attendance">
              <span className="action-icon"><CheckCircle size={24} weight="duotone" /></span>
              <span>Take Attendance</span>
            </Link>
            <Link to="/announcements" className="action-card" data-testid="quick-announcement">
              <span className="action-icon"><Megaphone size={24} weight="duotone" /></span>
              <span>New Announcement</span>
            </Link>
          </div>
        </div>
      </div>

    </Layout>
  );
}

export default DashboardPage;
